import { useRef, useState, useMemo } from "react";
import ValidationErrors from "../ValidationErrors/ValidationErrors";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import classes from "./Textarea.module.css";
import useFormFieldAccess from "../../../hooks/useFormFieldAccess";
import checkFieldDisabledStatus from "../../../utils/accessUtils/checkFieldDisabledStatus";
import extractFieldValidations from "../../../utils/accessUtils/extractFieldValidations";
import validate from "../../../utils/validations/validateV2";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  pStringDecoder,
  pStringEncoder,
  removeEncodeDecodeParts,
} from "../../../utils/textUtils/pEncodeDecode";

const Textarea = ({
  type = "text",
  styleType,
  validityName,
  disabled,
  processualDisabled,
  pageIsProcessual,
  entityIsProcessual,
  skipStateCanEditThisFieldCheck,
  currentState,
  value,
  onChange,
  onBlur,
  label,
  name,
  maxLength,
  uniqueLabelId,
  ltrInput,
  fieldAccesses,
  fieldNames = [],
  helperTxt,
  onFocus,
  fieldAddress,
  fieldName,
  skipFieldAccessCheck,
  validateHandler,
  localValidationInfos,
  confirmActionOrder,
  forceEn,
  textareaStyle,
  tooltipContent,
  notif,
  encodeDecodeStrAllowed = false,
  uiStyle,
  isMongoField,
  hasSQLEntity,
  mongoFieldPath,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { disabled: fieldIsDisabled, processualDisabled: fieldIsProcessualDisabled } = useMemo(
    () =>
      checkFieldDisabledStatus({
        fieldAccesses,
        fieldName,
        pageIsProcessual,
        disabledFromParent: disabled,
        processualDisabledFromParent: processualDisabled,
        currentState,
        entityIsProcessual,
        skipStateCanEditThisFieldCheck,
        isMongoField,
        hasSQLEntity,
        mongoFieldPath: mongoFieldPath ? mongoFieldPath : fieldAddress ? fieldAddress : null,
      }),
    [fieldAccesses, disabled, processualDisabled, entityIsProcessual ? currentState : undefined],
  );
  const { inWhichStateOrderItBecomesNecessary, validationType, isMandatory } = useMemo(
    () =>
      extractFieldValidations({
        fieldAccesses,
        fieldName,
      }),
    [fieldAccesses],
  );
  const debounceTimeoutRef = useRef(null);

  const autoAdjustHeight = (event) => {
    event.target.style.height = "auto";
    event.target.style.height = event.target.scrollHeight + "px";
  };
  const changeHandler = (e) => {
    let value = e.target.value || null;

    if (value) {
      if (type === "textOnly") {
        value = value.replace(/[0-9]/g, "");
      }
      if (encodeDecodeStrAllowed) {
        value = pStringEncoder(value);
      } else {
        value = removeEncodeDecodeParts(value);
      }
    }
    const properObj = {
      target: { name: e.target.name, value },
    };
    // onChange(e.target.name, e.target.value === "" ? null : e.target.value, e);
    onChange(e.target.name, value, properObj);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      autoAdjustHeight(e);
    }, 3000);
  };
  const focusHandler = (e) => {
    if (onFocus) onFocus(e, fieldAddress);
    setIsFocused(true);
  };
  const blurHandler = (e) => {
    if (onBlur) {
      onBlur(e, fieldAddress);
    }
    if (validateHandler) {
      const validateOptions = { ...localValidationInfos };
      if (isMandatory) {
        validateOptions.isOptional = false;
      } else {
        validateOptions.isOptional = true;
      }
      if (validationType) validateOptions.type = validationType;
      if (inWhichStateOrderItBecomesNecessary)
        validateOptions.inWhichStateOrderItBecomesNecessary = inWhichStateOrderItBecomesNecessary;
      if (confirmActionOrder) validateOptions.clickedActionOrder = confirmActionOrder;
      if (forceEn) validateOptions.forceLtr = forceEn;
      validateOptions.data = e.target.value;

      const newValue = validate(validateOptions);
      validateHandler(newValue, fieldAddress);
    }
    setIsFocused(false);
  };
  const readOnly = skipFieldAccessCheck ? disabled : fieldIsDisabled || fieldIsProcessualDisabled;
  return (
    <div className={[classes.container, uiStyle ? classes[`${uiStyle}Container`] : ""].join(" ")}>
      <label
        className={[
          classes.label,
          styleType === "osp"
            ? [classes.fSixBackground, classes.labelMrginStart].join(" ")
            : classes.transparentBgc,
          isFocused || value
            ? styleType === "osp"
              ? classes.labelUpMore
              : classes.labelUp
            : classes.labelDown,
          readOnly
            ? classes.disabledColor
            : isFocused
              ? styleType === "osp"
                ? classes.pinkColor
                : classes.purpleColor
              : validityName && !validityName?.isValid
                ? classes.redColor
                : classes.greyColor,
        ].join(" ")}
        htmlFor={uniqueLabelId ?? name}
      >
        {label}
        {isMandatory || localValidationInfos?.type === "mandatory" ? (
          <span className={classes.mandatorySign}>*</span>
        ) : null}
        {notif === true ? <ErrorOutlineOutlinedIcon className={classes.notif} /> : null}
      </label>
      {tooltipContent ? <span className={classes.tooltip}>{tooltipContent}</span> : null}
      <textarea
        name={name}
        id={uniqueLabelId ?? name}
        autoComplete="off"
        value={value === null ? "" : encodeDecodeStrAllowed ? pStringDecoder(value) : value}
        maxLength={maxLength}
        type="text"
        style={textareaStyle}
        disabled={readOnly}
        className={[
          classes.textarea,
          uiStyle
            ? ""
            : styleType === "osp"
              ? [classes.allBorderTextarea, classes.allBorder].join(" ")
              : [classes.bottomBorderTextArea, classes.bottomBorder].join(" "),
          ltrInput ? classes.ltrInput : null,
          uiStyle
            ? classes[`${uiStyle}Textarea`]
            : readOnly
              ? styleType === "osp"
                ? classes.disabledOspBorder
                : classes.disabledBottomBorder
              : isFocused
                ? styleType === "osp"
                  ? classes.pinkBorder
                  : classes.purpleBorder
                : validityName && !validityName?.isValid
                  ? classes.redBorder
                  : classes.greyBorder,
        ].join(" ")}
        onFocus={focusHandler}
        onChange={changeHandler}
        onBlur={blurHandler}
      />
      {helperTxt ? (
        <span className={classes.helperTxtContainer}>
          <span className={classes.helperTxtIcon}>
            <HelpOutlineOutlinedIcon className={classes.questionIcon} />
          </span>
          <span className={classes.helperTxt}> {helperTxt}</span>
        </span>
      ) : null}
      {validityName ? (
        <ValidationErrors validity={validityName?.isValid} message={validityName?.msg} />
      ) : null}
    </div>
  );
};

export default Textarea;
